import React, { useRef, useState } from "react";

// URLs de carga (API Gateway -> S3 incoming/, staging). Se inyectan en build por entorno
// desde el workflow. Punto único para los 2 archivos — la Lambda incoming-router detecta
// por cabeceras si es el Maestro de certificaciones o TeamColombia.xlsx y lo enruta sola,
// no hace falta elegir acá.
// REACT_APP_UPLOAD_URL      → PUT incoming/upload.csv  (para .csv)
// REACT_APP_UPLOAD_URL_XLSX → PUT incoming/upload.xlsx (para .xlsx)
const UPLOAD_URL      = process.env.REACT_APP_UPLOAD_URL      || "";
const UPLOAD_URL_XLSX = process.env.REACT_APP_UPLOAD_URL_XLSX || "";

const COLORS = { brand: "#004b8d", dark: "#003366", ok: "#15803d", warn: "#d97706", err: "#b91c1c" };

export default function App() {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | uploading | success | error
  const [message, setMessage] = useState("");
  const inputRef = useRef(null);

  const pickFile = (f) => {
    setStatus("idle");
    setMessage("");
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!name.endsWith(".csv") && !name.endsWith(".xlsx")) {
      setFile(null);
      setStatus("error");
      setMessage("El archivo debe ser .xlsx o .csv (Maestro de certificaciones o TeamColombia).");
      return;
    }
    if (f.size === 0) {
      setFile(null);
      setStatus("error");
      setMessage("El archivo está vacío.");
      return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) {
      setStatus("error");
      setMessage("Selecciona primero el archivo.");
      return;
    }
    const isXlsx = file.name.toLowerCase().endsWith(".xlsx");
    const targetUrl = isXlsx ? UPLOAD_URL_XLSX : UPLOAD_URL;
    if (!targetUrl) {
      setStatus("error");
      setMessage("Configuración incompleta: falta la URL de carga.");
      return;
    }
    setStatus("uploading");
    setMessage("Subiendo el archivo…");
    try {
      const contentType = isXlsx
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "text/csv";
      const res = await fetch(targetUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      });
      if (res.ok) {
        setStatus("success");
        setMessage(
          "Archivo recibido. El procesamiento corre automáticamente; recibirás el resultado (éxito o alerta) por correo en unos minutos."
        );
        setFile(null);
        if (inputRef.current) inputRef.current.value = "";
      } else {
        const text = await res.text().catch(() => "");
        setStatus("error");
        setMessage(`No se pudo subir el archivo (HTTP ${res.status}). ${text}`.trim());
      }
    } catch (err) {
      setStatus("error");
      setMessage(`Error de conexión: ${err.message}`);
    }
  };

  const msgColor =
    status === "success" ? COLORS.ok : status === "error" ? COLORS.err : status === "uploading" ? COLORS.warn : "#333";
  const busy = status === "uploading";

  return (
    <div style={{ fontFamily: "Arial, sans-serif", maxWidth: 620, margin: "auto", padding: "2rem", lineHeight: 1.6 }}>
      <h2 style={{ textAlign: "center", color: COLORS.dark, marginBottom: "0.25rem" }}>
        Cargar Maestro de certificaciones o TeamColombia
      </h2>
      <p style={{ textAlign: "center", color: "#555", marginTop: 0 }}>
        Sube el archivo <b>.xlsx</b> o <b>.csv</b> del Maestro de certificaciones, o el{" "}
        <b>TeamColombia.xlsx</b> de empleados — el sistema reconoce cuál es y lo procesa solo. El
        resto del proceso es automático.
      </p>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          pickFile(e.dataTransfer.files?.[0]);
        }}
        style={{
          border: `2px dashed ${COLORS.brand}`,
          borderRadius: 10,
          padding: "1.5rem",
          textAlign: "center",
          background: "#f7fafc",
          marginTop: "1.25rem",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => pickFile(e.target.files?.[0])}
          disabled={busy}
          style={{ width: "100%" }}
        />
        {file && (
          <p style={{ marginBottom: 0, color: "#444" }}>
            Seleccionado: <b>{file.name}</b> ({(file.size / 1024).toFixed(1)} KB)
          </p>
        )}
      </div>

      <button
        onClick={handleUpload}
        disabled={busy || !file}
        style={{
          marginTop: "1.25rem",
          width: "100%",
          padding: "0.9rem 1.5rem",
          border: "none",
          borderRadius: 8,
          fontWeight: "bold",
          color: "white",
          cursor: busy || !file ? "not-allowed" : "pointer",
          backgroundColor: busy || !file ? "#9ca3af" : COLORS.brand,
          transition: "background 0.3s ease",
        }}
      >
        {busy ? "Subiendo…" : "Subir archivo"}
      </button>

      {message && (
        <p style={{ marginTop: "1.25rem", color: msgColor, fontWeight: "bold", textAlign: "center" }}>{message}</p>
      )}
    </div>
  );
}
